/**
 * Sales scoreboard · data hooks (Backend PLAN §3).
 *
 *   useScoreboardAccess()          GET /api/v1/sales/scoreboard/me?period=TODAY — one shared read
 *   useScoreboardTeam(sel)         GET /api/v1/sales/scoreboard/team?period…        — 60 s poll
 *   useScoreboardRep(id, sel, by)  GET /api/v1/sales/scoreboard/rep/{id}?period…&groupBy
 *   useScoreboardMe(sel)           GET /api/v1/sales/scoreboard/me?period…
 *   useScoreboardRecords(q)        GET /api/v1/sales/scoreboard/records?metric&userId&period…
 *   useScoreboardSettings()        GET/PUT /api/v1/sales-settings/scoreboard (+ rebuild)
 *   useDayReport(on)               GET /api/v1/sales/day-report/me · POST …/me/send
 *   usePageOpened(page, on)        POST /api/v1/sales/scoreboard/usage (fire-and-forget)
 *   useMetricDictionary()          GET /api/v1/sales/scoreboard/metrics — once per session, shared
 *   useCallClassification(q)      GET /api/v1/sales/scoreboard/classify-call?customerId&leadId
 *
 * Every read keeps the server's errorCode, so a screen can tell "scoreboard off" (404
 * SCOREBOARD_DISABLED → show nothing, look exactly as before) from a real failure.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { ApiError, authFetch } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { useVisibleInterval } from '../hooks/usePoolDispatch'
import { useLastPulse } from '../hooks/useHotLeadBadge'
import { accessFrom, classifyFailure, type AccessState, type ScoreboardFailure } from './access'
import { periodQuery, type PeriodSelection } from './period'
import type { RecordsMetric } from './recordsContext'
import type {
  AgreedAction, CallClassification, DayReportResponse, DayReportsResponse, MetricDefinitionDto, NeedsYouRecordsResponse,
  NudgeResponse, WeekFocus, MetricId, MyScoreboardResponse, RebuildRun, RepScoreboardResponse, ScoreboardRecordsResponse,
  ScoreboardSettingsResponse, SelfReportedItem, TeamScoreboardResponse, UpdateScoreboardSettingsRequest, UsageEvent,
} from './types'

interface Envelope<T> {
  success: boolean
  message?: string
  data?: T
}

export interface QueryState<T> {
  data: T | null
  hasLoaded: boolean
  error: string | null
  failure: ScoreboardFailure | null
  /** A read the screen asked for (refetch) is in flight; the minute poll stays silent. */
  isLoading: boolean
  refetch: () => Promise<void>
}

function failureOf(e: unknown): { message: string; failure: ScoreboardFailure } {
  if (e instanceof ApiError) {
    const code = (e.data as { errorCode?: string } | null)?.errorCode
    return { message: e.message, failure: classifyFailure(e.status, code) }
  }
  return { message: e instanceof Error ? e.message : 'Failed to load', failure: 'ERROR' }
}

/** GET `path` (null = off); re-read every `pollMs` while the tab is visible (0 = once). */
export function useScoreboardQuery<T>(path: string | null, pollMs = 60_000): QueryState<T> {
  const token = useAuthStore((s) => s.tokens?.accessToken)
  const [state, setState] = useState<{ path: string | null; data: T | null; error: string | null; failure: ScoreboardFailure | null }>(
    { path: null, data: null, error: null, failure: null },
  )
  const [loading, setLoading] = useState(false)
  const seq = useRef(0)

  const fetchNow = useCallback(async (quiet = false) => {
    if (!path || !token) return
    const mine = ++seq.current
    if (!quiet) setLoading(true)
    try {
      const resp = await authFetch<Envelope<T>>(path, token, { suppressPermissionToast: true })
      if (mine !== seq.current) return
      if (resp.success && resp.data != null) setState({ path, data: resp.data, error: null, failure: null })
      else setState((p) => ({ ...p, path, error: resp.message ?? 'Failed to load', failure: 'ERROR' }))
    } catch (e) {
      if (mine !== seq.current) return
      const f = failureOf(e)
      // A transient failure keeps the last good numbers on screen; "off" clears them.
      setState((p) =>
        p.path === path && f.failure === 'ERROR'
          ? { ...p, error: f.message, failure: f.failure }
          : { path, data: null, error: f.message, failure: f.failure },
      )
    } finally {
      if (mine === seq.current) setLoading(false)
    }
  }, [path, token])
  const refetch = useCallback(() => fetchNow(), [fetchNow])

  useVisibleInterval(() => void fetchNow(true), pollMs > 0 ? pollMs : 24 * 60 * 60 * 1000, !!path && !!token, path)

  const current = state.path === path
  return {
    data: current ? state.data : null,
    hasLoaded: current && (state.data !== null || state.error !== null),
    error: current ? state.error : null,
    failure: current ? state.failure : null,
    isLoading: current && loading,
    refetch,
  }
}

// ── shared access (/me today) ────────────────────────────────────────────

/**
 * One read of `/me?period=TODAY` shared by the sidebar, My day and the logging surfaces, so a
 * tenant without the scoreboard pays one 404 per few minutes, not one per component.
 */
const ACCESS_TTL_MS = 60_000
const DISABLED_TTL_MS = 5 * 60_000

let accessMe: MyScoreboardResponse | null = null
let accessFailure: ScoreboardFailure | null = null
let accessAt = 0
/** The cache is per PERSON, not per token: a silent token refresh must not reset it (M1). */
let accessUser: string | null = null
let inFlight: Promise<void> | null = null
const accessListeners = new Set<() => void>()
let accessVersion = 0
/** Refreshes a screen asked for (after a logged call, a done action) still in flight. */
let accessRefreshing = 0

function notifyAccess() {
  accessVersion++
  for (const l of accessListeners) l()
}

/**
 * How a refresh treats the cache:
 *  - 'cached' — only when the entry is stale (60 s on, 5 min off);
 *  - 'poll'   — a page's periodic re-check: always re-reads while ON, but while OFF still at most
 *               once per 5 min (a tenant without the scoreboard pays one 404 per 5 min, no more);
 *  - 'always' — a settings change that may have switched it on or off.
 */
export type AccessRefresh = 'cached' | 'poll' | 'always'

export function accessStale(
  mode: AccessRefresh,
  entry: { user: string | null; at: number; off: boolean },
  user: string,
  now: number,
): boolean {
  if (mode === 'always' || entry.user !== user) return true
  const age = now - entry.at
  if (entry.off) return age >= DISABLED_TTL_MS
  return mode === 'poll' || age >= ACCESS_TTL_MS
}

export async function refreshScoreboardAccess(token: string, userId: string, mode: AccessRefresh = 'cached'): Promise<void> {
  if (!accessStale(mode, { user: accessUser, at: accessAt, off: accessFailure !== null }, userId, Date.now())) return
  if (inFlight) return inFlight
  inFlight = (async () => {
    try {
      const resp = await authFetch<Envelope<MyScoreboardResponse>>('/api/v1/sales/scoreboard/me?period=TODAY', token, {
        suppressPermissionToast: true,
      })
      accessMe = resp.success && resp.data ? resp.data : null
      accessFailure = accessMe ? null : 'ERROR'
    } catch (e) {
      const f = failureOf(e).failure
      // A transient error keeps a known "on" state; anything definite replaces it.
      if (f !== 'ERROR' || !accessMe) {
        accessMe = null
        accessFailure = f
      }
    } finally {
      accessAt = Date.now()
      accessUser = userId
      inFlight = null
      notifyAccess()
    }
  })()
  return inFlight
}

function subscribeAccess(l: () => void) {
  accessListeners.add(l)
  return () => accessListeners.delete(l)
}

const getAccessVersion = () => accessVersion

export interface ScoreboardAccessResult {
  access: AccessState
  /** Today's `/me` (null while unknown or off). */
  me: MyScoreboardResponse | null
  /** A `refresh()` is in flight (the background poll does not count). */
  refreshing: boolean
  refresh: () => void
}

/**
 * `enabled` false = do not read (e.g. no sales seat): stays "unknown", renders nothing new.
 * "unknown" (not asked yet) is never "off": callers keep whatever is already open while a
 * re-check runs, and the cached answer survives token refreshes.
 */
export function useScoreboardAccess(enabled = true, pollMs = 0): ScoreboardAccessResult {
  const token = useAuthStore((s) => s.tokens?.accessToken)
  const userId = useAuthStore((s) => s.user?.userId ?? null)
  useSyncExternalStore(subscribeAccess, getAccessVersion, getAccessVersion)
  const pulse = useLastPulse()
  const run = !!token && !!userId && enabled
  // The token is read through a ref so a refreshed token does not re-run the effect.
  const tokenRef = useRef(token)
  useEffect(() => {
    tokenRef.current = token
  })
  useEffect(() => {
    if (run && tokenRef.current && userId) void refreshScoreboardAccess(tokenRef.current, userId)
  }, [run, userId])
  useVisibleInterval(() => {
    if (tokenRef.current && userId) void refreshScoreboardAccess(tokenRef.current, userId, 'poll')
  }, pollMs > 0 ? pollMs : 24 * 60 * 60 * 1000, run && pollMs > 0)
  const refresh = useCallback(() => {
    if (!tokenRef.current || !userId) return
    accessRefreshing++
    notifyAccess()
    void refreshScoreboardAccess(tokenRef.current, userId, 'poll').finally(() => {
      accessRefreshing--
      notifyAccess()
    })
  }, [userId])
  if (!run || accessUser !== userId) return { access: { kind: 'unknown' }, me: null, refreshing: false, refresh }
  return { access: accessFrom(accessMe, accessFailure, pulse?.isManager), me: accessMe, refreshing: accessRefreshing > 0, refresh }
}

// ── reads ────────────────────────────────────────────────────────────────

export function useScoreboardTeam(sel: PeriodSelection, enabled = true) {
  return useScoreboardQuery<TeamScoreboardResponse>(
    enabled ? `/api/v1/sales/scoreboard/team?${periodQuery(sel)}` : null,
  )
}

export function useScoreboardRep(userId: string | null, sel: PeriodSelection, groupBy: 'day' | 'week') {
  return useScoreboardQuery<RepScoreboardResponse>(
    userId ? `/api/v1/sales/scoreboard/rep/${encodeURIComponent(userId)}?${periodQuery(sel, { groupBy })}` : null,
  )
}

export function useScoreboardMe(sel: PeriodSelection, enabled = true) {
  return useScoreboardQuery<MyScoreboardResponse>(enabled ? `/api/v1/sales/scoreboard/me?${periodQuery(sel)}` : null)
}

export interface RecordsQuery {
  metric: RecordsMetric
  userId: string
  sel: PeriodSelection
}

export function useScoreboardRecords(q: RecordsQuery | null) {
  return useScoreboardQuery<ScoreboardRecordsResponse>(
    q ? `/api/v1/sales/scoreboard/records?${periodQuery(q.sel, { metric: q.metric, userId: q.userId })}` : null,
    0,
  )
}

// ── settings + rebuild ───────────────────────────────────────────────────

export interface WriteResult<T> {
  success: boolean
  data?: T
  message?: string
  errorCode?: string
  status?: number
}

function writeFailure<T>(e: unknown): WriteResult<T> {
  if (e instanceof ApiError) {
    return { success: false, message: e.message, status: e.status, errorCode: (e.data as { errorCode?: string } | null)?.errorCode }
  }
  return { success: false, message: e instanceof Error ? e.message : 'Request failed' }
}

const SETTINGS_PATH = '/api/v1/sales-settings/scoreboard'

export function useScoreboardSettings(enabled = true) {
  const token = useAuthStore((s) => s.tokens?.accessToken)
  const q = useScoreboardQuery<ScoreboardSettingsResponse>(enabled ? SETTINGS_PATH : null, 0)
  const { refetch } = q

  const save = useCallback(
    async (body: UpdateScoreboardSettingsRequest): Promise<WriteResult<ScoreboardSettingsResponse>> => {
      if (!token) return { success: false, message: 'Not authenticated' }
      try {
        const resp = await authFetch<Envelope<ScoreboardSettingsResponse>>(SETTINGS_PATH, token, {
          method: 'PUT',
          body: JSON.stringify(body),
        })
        await refetch()
        // Switching it on or off changes what every sales screen shows.
        const uid = useAuthStore.getState().user?.userId
        if (uid) void refreshScoreboardAccess(token, uid, 'always')
        return { success: resp.success, data: resp.data, message: resp.message }
      } catch (e) {
        return writeFailure(e)
      }
    },
    [token, refetch],
  )

  const startRebuild = useCallback(
    async (body: { from?: string; dryRun: boolean }): Promise<WriteResult<RebuildRun>> => {
      if (!token) return { success: false, message: 'Not authenticated' }
      try {
        const resp = await authFetch<Envelope<RebuildRun>>(`${SETTINGS_PATH}/rebuild`, token, {
          method: 'POST',
          body: JSON.stringify(body),
        })
        return { success: resp.success, data: resp.data, message: resp.message }
      } catch (e) {
        return writeFailure(e)
      }
    },
    [token],
  )

  const getRun = useCallback(
    async (runId: string): Promise<WriteResult<RebuildRun>> => {
      if (!token) return { success: false, message: 'Not authenticated' }
      try {
        const resp = await authFetch<Envelope<RebuildRun>>(`${SETTINGS_PATH}/rebuild/${encodeURIComponent(runId)}`, token, {
          suppressPermissionToast: true,
        })
        return { success: resp.success, data: resp.data, message: resp.message }
      } catch (e) {
        return writeFailure(e)
      }
    },
    [token],
  )

  return { ...q, save, startRebuild, getRun }
}

// ── day report (P2.8) ────────────────────────────────────────────────────

export function useDayReport(enabled: boolean, date?: string | null) {
  const token = useAuthStore((s) => s.tokens?.accessToken)
  const path = enabled ? `/api/v1/sales/day-report/me${date ? `?date=${encodeURIComponent(date)}` : ''}` : null
  const q = useScoreboardQuery<DayReportResponse>(path, 0)
  const [sent, setSent] = useState<DayReportResponse | null>(null)

  const send = useCallback(
    async (selfReported: SelfReportedItem[], forDate?: string): Promise<WriteResult<DayReportResponse>> => {
      if (!token) return { success: false, message: 'Not authenticated' }
      try {
        const resp = await authFetch<Envelope<DayReportResponse>>('/api/v1/sales/day-report/me/send', token, {
          method: 'POST',
          body: JSON.stringify({ date: forDate ?? date ?? undefined, selfReported }),
        })
        if (resp.data) setSent(resp.data)
        return { success: resp.success, data: resp.data, message: resp.message }
      } catch (e) {
        return writeFailure(e)
      }
    },
    [token, date],
  )

  // The send answer is the freshest copy until the next read replaces it.
  const data = sent && q.data && sent.date === q.data.date && (sent.sendCount ?? 0) >= (q.data.sendCount ?? 0) ? sent : q.data
  return { ...q, data, send }
}

/** Leaders: the day reports of their scope (or one rep) for a date (default today). */
export function useDayReports(enabled: boolean, opts: { date?: string | null; userId?: string | null } = {}) {
  const q = new URLSearchParams()
  if (opts.date) q.set('date', opts.date)
  if (opts.userId) q.set('userId', opts.userId)
  const qs = q.toString()
  return useScoreboardQuery<DayReportsResponse>(enabled ? `/api/v1/sales/day-reports${qs ? `?${qs}` : ''}` : null, 0)
}

// ── adoption telemetry (E12) ─────────────────────────────────────────────

export function postUsage(token: string, event: UsageEvent, page?: string): void {
  void authFetch('/api/v1/sales/scoreboard/usage', token, {
    method: 'POST',
    body: JSON.stringify(page ? { event, page } : { event }),
    suppressPermissionToast: true,
  }).catch(() => {
    /* fire-and-forget — telemetry never interrupts the page */
  })
}

/** Record one PAGE_OPENED per mount of `page`, once the scoreboard is known to be on. */
export function usePageOpened(page: string, on: boolean): void {
  const token = useAuthStore((s) => s.tokens?.accessToken)
  const sentFor = useRef<string | null>(null)
  useEffect(() => {
    if (!on || !token || sentFor.current === page) return
    sentFor.current = page
    postUsage(token, 'PAGE_OPENED', page)
  }, [on, token, page])
}

// ── metric dictionary (P2.12) ────────────────────────────────────────────

/**
 * The server's metric registry — every label and definition on every screen. Static, served
 * whether or not the tenant has the scoreboard on, so it is read once per session and shared.
 * Until it arrives (or if it fails) labels fall back to the column headers and tooltips are absent.
 */
let dictionary: Partial<Record<MetricId, MetricDefinitionDto>> | null = null
let dictionaryToken: string | null = null
let dictionaryLoading = false
const dictionaryListeners = new Set<() => void>()
const getDictionary = () => dictionary

function subscribeDictionary(l: () => void) {
  dictionaryListeners.add(l)
  return () => dictionaryListeners.delete(l)
}

async function loadDictionary(token: string) {
  if (dictionaryLoading || (dictionary && dictionaryToken === token)) return
  dictionaryLoading = true
  try {
    const resp = await authFetch<Envelope<MetricDefinitionDto[]>>('/api/v1/sales/scoreboard/metrics', token, {
      suppressPermissionToast: true,
    })
    if (resp.success && Array.isArray(resp.data)) {
      dictionary = Object.fromEntries(resp.data.map((m) => [m.id, m])) as Partial<Record<MetricId, MetricDefinitionDto>>
      dictionaryToken = token
      for (const l of dictionaryListeners) l()
    }
  } catch {
    /* labels fall back to column headers; a later mount retries */
  } finally {
    dictionaryLoading = false
  }
}

export function useMetricDictionary(): Partial<Record<MetricId, MetricDefinitionDto>> | null {
  const token = useAuthStore((s) => s.tokens?.accessToken)
  const dict = useSyncExternalStore(subscribeDictionary, getDictionary, getDictionary)
  useEffect(() => {
    if (token) void loadDictionary(token)
  }, [token])
  return dict
}

// ── call classification (P2.6) ───────────────────────────────────────────

/** What a call logged now would count as. Any failure (404, scoreboard off, error) → null, silently. */
export function useCallClassification(q: { customerId?: string | null; leadId?: string | null } | null): CallClassification | null {
  const qs = new URLSearchParams()
  if (q?.customerId) qs.set('customerId', q.customerId)
  if (q?.leadId) qs.set('leadId', q.leadId)
  const path = q && (q.customerId || q.leadId) ? `/api/v1/sales/scoreboard/classify-call?${qs.toString()}` : null
  const res = useScoreboardQuery<CallClassification>(path, 0)
  return res.failure ? null : res.data
}

// ── action loop (P3) ─────────────────────────────────────────────────────

/** The records behind a Needs-you item (its own `recordsQuery` path; null = closed). */
export function useNeedsYouRecords(path: string | null) {
  return useScoreboardQuery<NeedsYouRecordsResponse>(path, 0)
}

export function useActionLoop() {
  const token = useAuthStore((s) => s.tokens?.accessToken)

  const call = useCallback(
    async <T,>(path: string, method: 'POST' | 'PUT' | 'PATCH', body: unknown): Promise<WriteResult<T>> => {
      if (!token) return { success: false, message: 'Not authenticated' }
      try {
        const resp = await authFetch<Envelope<T> | null>(path, token, {
          method,
          body: JSON.stringify(body),
          // 429 NUDGE_CAP and 403s are explained in place, not by the global toast.
          suppressPermissionToast: true,
        })
        // 200 or 201 (agreed actions are created with 201); any 2xx is success, even with no body.
        if (!resp) return { success: true }
        return { success: resp.success !== false, data: resp.data, message: resp.message }
      } catch (e) {
        return writeFailure(e)
      }
    },
    [token],
  )

  /**
   * POST nudge — the server composes the rep's own text from the item (never the head's sentence,
   * which names other reps). 429 `NUDGE_CAP` = already nudged today (one per rep per day, E11).
   */
  const nudge = useCallback(
    (userId: string, itemId: string) =>
      call<NudgeResponse>(`/api/v1/sales/scoreboard/nudge/${encodeURIComponent(userId)}`, 'POST', { itemId }),
    [call],
  )

  const setFocus = useCallback(
    (body: { metric?: MetricId; text?: string; note?: string; weekStart?: string }) =>
      call<WeekFocus>('/api/v1/sales/scoreboard/focus', 'PUT', body),
    [call],
  )

  const addAction = useCallback(
    (userId: string, body: { text: string; dueDate?: string; metric?: MetricId }) =>
      call<AgreedAction>(`/api/v1/sales/scoreboard/rep/${encodeURIComponent(userId)}/actions`, 'POST', body),
    [call],
  )

  const setActionStatus = useCallback(
    (userId: string, actionId: string, status: 'DONE' | 'DROPPED') =>
      call<AgreedAction>(
        `/api/v1/sales/scoreboard/rep/${encodeURIComponent(userId)}/actions/${encodeURIComponent(actionId)}`,
        'PATCH',
        { status },
      ),
    [call],
  )

  return { nudge, setFocus, addAction, setActionStatus }
}
